/**
 * Centralized Environment Configuration
 *
 * Single source of truth for all environment variables and derived configuration.
 * Import `env` from this module instead of accessing import.meta.env directly.
 */

/**
 * All environment variables used in the application.
 * Provides centralized access with type safety.
 */
export const env = {
  // Multi-tenancy
  tenantSlug: import.meta.env.VITE_TENANT_SLUG || "absolut-radio",

  // Site Configuration
  siteUrl: import.meta.env.VITE_SITE_URL || "https://absolutradio.de",

  // API Endpoints - Required
  apiMetadataEndpoint: import.meta.env.VITE_API_METADATA_ENDPOINT as string | undefined,
  apiStationEndpoint: import.meta.env.VITE_API_STATION_ENDPOINT as string | undefined,

  // API Endpoints - Optional
  apiIcyMetadataEndpoint: import.meta.env.VITE_API_ICY_METADATA_ENDPOINT as string | undefined,
  apiVideoEndpoint: import.meta.env.VITE_API_VIDEO_ENDPOINT as string | undefined,

  // CMS Configuration
  cmsApiUrl: import.meta.env.VITE_CMS_API_URL || "http://localhost:3000",

  // YouTube Integration
  youtubeApiKey: import.meta.env.VITE_YOUTUBE_API_KEY as string | undefined,
  youtubeId: import.meta.env.VITE_YOUTUBE_ID as string | undefined,

  // Friendly Captcha
  friendlyCaptchaSiteKey: import.meta.env.VITE_FRIENDLY_CAPTCHA_SITE_KEY as string | undefined,

  // Cookie Consent
  usercentricsSettingsId: import.meta.env.VITE_USERCENTRICS_SETTINGS_ID as string | undefined,

  // Utiq consent SDK (separate pop-up model, ADE-702)
  utiqScriptUrl: import.meta.env.VITE_UTIQ_SCRIPT_URL as string | undefined,
  // Opt-in gate (ADE-868): Utiq stays off everywhere unless explicitly enabled
  // per-environment. Build mode (import.meta.env.PROD) can't be used — staging
  // is also a production build — so this is a deploy-time flag, not a mode check.
  utiqEnabled: import.meta.env.VITE_UTIQ_ENABLED === "true",

  // Google Tag Manager / Stape
  stapeUrl: import.meta.env.VITE_STAPE_URL || "https://gtm.absolutradio.de",
  stapeLoaderPath: import.meta.env.VITE_STAPE_LOADER_PATH as string | undefined,
  stapeAuthToken: import.meta.env.VITE_STAPE_AUTH_TOKEN as string | undefined,
  gtmContainerId: import.meta.env.VITE_GTM_CONTAINER_ID as string | undefined,

  // Secrets (server-only at runtime, accessed via process.env)
  revalidateSecret: process.env.REVALIDATE_SECRET as string | undefined,

  // Feature flags
  dataSource: import.meta.env.VITE_DATA_SOURCE as "cms" | "legacy" | undefined,
  debugEnabled: import.meta.env.VITE_DEBUG_ENABLED as string | undefined,

  // External URLs
  landingPageUrl: import.meta.env.VITE_LANDING_PAGE_URL || "https://landing.absolutradio.de",

  // Environment flags
  isDev: import.meta.env.DEV as boolean,
  isProd: import.meta.env.PROD as boolean,
  mode: import.meta.env.MODE as string,
} as const;

// ─── Required Variable Validation ─────────────────────────────────────────────
// Warn at module load for required vars without defaults that are missing.

const requiredVars: Array<[keyof typeof env, string]> = [
  ["apiMetadataEndpoint", "VITE_API_METADATA_ENDPOINT"],
  ["apiStationEndpoint", "VITE_API_STATION_ENDPOINT"],
  ["usercentricsSettingsId", "VITE_USERCENTRICS_SETTINGS_ID"],
  ["gtmContainerId", "VITE_GTM_CONTAINER_ID"],
  ["stapeLoaderPath", "VITE_STAPE_LOADER_PATH"],
  ["stapeAuthToken", "VITE_STAPE_AUTH_TOKEN"],
];

for (const [key, envVar] of requiredVars) {
  if (!env[key]) {
    console.warn(`[env] Missing required variable ${envVar} — feature may not work`);
  }
}
